import React from 'react';
import { formatMonthKey } from '../utils/dateUtils';
import { formatCurrency } from '../utils/formatters';

const HistoryView = ({ monthlyData, initializeEntryData, setSelectedMonth, setEditMode, setCurrentView }) => {
  if (monthlyData.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 text-sm">No history yet. Start by entering your first month of data.</p>
      </div>
    );
  }

  const sorted = [...monthlyData].sort((a, b) => b.month.localeCompare(a.month));

  const nwChange = (current, previous) => {
    if (previous == null) return null;
    const delta = current - previous;
    const pct = previous !== 0 ? (delta / Math.abs(previous)) * 100 : 0;
    return { delta, pct };
  };

  const first = sorted[sorted.length - 1];
  const latest = sorted[0];
  const totalGrowth = monthlyData.length > 1 ? latest.netWorth - first.netWorth : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Months Tracked', value: monthlyData.length },
          { label: 'Current Net Worth', value: formatCurrency(latest.netWorth) },
          { label: 'Total Growth', value: totalGrowth != null ? formatCurrency(totalGrowth) : '—' },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-card px-5 py-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{label}</p>
            <p className="text-lg font-semibold tracking-tight text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-card overflow-hidden">
        {/* Column headers — desktop only */}
        <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_1fr_64px] px-6 py-3 border-b border-gray-100 bg-gray-50">
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Month</span>
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide text-right">Assets</span>
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide text-right">Debts</span>
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide text-right">Net Worth</span>
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide text-right">MoM</span>
        </div>

        {/* Rows */}
        <div className="divide-y divide-gray-100">
          {sorted.map((data, index) => {
            const prev = sorted[index + 1];
            const change = nwChange(data.netWorth, prev?.netWorth);
            const positive = change?.delta >= 0;
            const onEdit = () => {
              setSelectedMonth(data.month);
              initializeEntryData(data.month);
              setEditMode(true);
              setCurrentView('entry');
            };
            const changeLabel = change == null ? '—' : `${positive ? '+' : ''}${change.pct.toFixed(1)}%`;
            const changeColor = change == null ? 'text-gray-300' : positive ? 'text-accent' : 'text-negative';

            return (
              <React.Fragment key={data.month}>
                {/* Mobile card row */}
                <button
                  onClick={onEdit}
                  className="sm:hidden w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-gray-900 truncate">{formatMonthKey(data.month)}</span>
                    {index === 0 && <span className="text-xs text-accent font-medium shrink-0">latest</span>}
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <span className="text-sm text-gray-900 font-medium tabular-nums">{formatCurrency(data.netWorth)}</span>
                    <span className={`text-xs tabular-nums ${changeColor}`}>{changeLabel}</span>
                  </div>
                </button>

                {/* Desktop row */}
                <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_1fr_64px] px-6 py-3.5 group hover:bg-gray-50 transition-colors">
                  {/* Month */}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-gray-900 truncate">{formatMonthKey(data.month)}</span>
                    {index === 0 && <span className="text-xs text-accent font-medium shrink-0">latest</span>}
                  </div>

                  {/* Assets */}
                  <span className="text-sm text-gray-500 text-right tabular-nums self-center">
                    {formatCurrency(data.totalAssets)}
                  </span>

                  {/* Debts */}
                  <span className="text-sm text-gray-500 text-right tabular-nums self-center">
                    {formatCurrency(data.totalDebts)}
                  </span>

                  {/* Net Worth */}
                  <span className="text-sm text-gray-900 font-medium text-right tabular-nums self-center">
                    {formatCurrency(data.netWorth)}
                  </span>

                  {/* MoM change + Edit */}
                  <div className="flex flex-col items-end justify-center gap-0.5">
                    <span className={`text-sm tabular-nums ${changeColor}`}>{changeLabel}</span>
                    <button
                      onClick={onEdit}
                      className="text-xs text-gray-400 hover:text-accent transition-colors opacity-0 group-hover:opacity-100"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HistoryView;
